"use client"

import { Link, useLocation } from "react-router-dom"
import { Col } from "reactstrap"
import { Href } from "../../../utils/Constant"
import { H4, Image } from "../../../AbstractElements"
import { dynamicImage } from "../../../Service"
import AuthService from "../../../Service/AuthService"
import { ProfileService, type UserProfile } from "../../../Service/ProfileService"
import { useEffect, useState, useRef } from "react"
import ProfileCompletionModal from "../../../Component/Application/Users/ProfileComplentionModal"
import { dE } from "@fullcalendar/core/internal-common"

interface Person {
  id: number
  first_name: string
  middle_name: string | null
  last_name: string
  full_name: string
  email: string
  phone: string
  id_doc_type: number | null
  id_doc_type_name: string | null
  id_doc_number: string | null
  id_doc_attachment: string | null
  photo_path: string | null
  created_at: string
  updated_at: string
  status: boolean
}

interface Role {
  id: number
  name: string
  assigned_at: string
}

interface DecodedTokenWithPerson {
  token_type: string
  exp: number
  iat: number
  jti: string
  user_id: number
  email: string
  is_staff: boolean
  is_superuser: boolean
  is_active: boolean
  email_verified: boolean
  is_registrar: boolean
  status: string
  roles?: Role[]
  person?: Person
}

const LeftHeader = () => {
  const [profileIncomplete, setProfileIncomplete] = useState(false)
  const [modalDismissed, setModalDismissed] = useState(false)
  const [currentProfile, setCurrentProfile] = useState<UserProfile | null>(null)
  const hasCalledAPI = useRef(false)
  const location = useLocation()

  const getUserData = (): DecodedTokenWithPerson | null => {
    try {
      const decodedToken = AuthService.getDecodedToken() as DecodedTokenWithPerson
      console.log('user information:', decodedToken)
      return decodedToken
    } catch (error) {
      console.error("Error getting user data from token:", error)
      return null
    }
  }

  const user = getUserData()
  const isOnProfilePage = location.pathname.includes("/users/useredit")

  // Fetch profile data ONCE - no retries
  useEffect(() => {
    if (!user || user.is_superuser || hasCalledAPI.current) return

    hasCalledAPI.current = true

    const fetchProfile = async () => {
      try {
        console.log("Fetching profile data once...")
        const profileData = await ProfileService.getProfile()
        setCurrentProfile(profileData)
        console.log("Profile data loaded successfully")
      } catch (error) {
        console.error("Failed to fetch profile (no retry):", error)
      }
    }

    fetchProfile()
  }, [user])

  // Check profile completion status
  useEffect(() => {
    if (!user || user.is_superuser) {
      setProfileIncomplete(false)
      return
    }

    const profileToCheck = currentProfile?.person || user.person

    if (profileToCheck) {
      const isIncomplete = !profileToCheck.id_doc_number || !profileToCheck.id_doc_type
      setProfileIncomplete(isIncomplete)
    } else {
      setProfileIncomplete(false)
    }
  }, [user, currentProfile])

  useEffect(() => {
    if (!isOnProfilePage) {
      setModalDismissed(false)
    }
  }, [isOnProfilePage])

  useEffect(() => {
    const handleProfileUpdate = () => {
      console.log("Profile update detected")
      hasCalledAPI.current = false
    }

    window.addEventListener("profileUpdated", handleProfileUpdate)
    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdate)
    }
  }, [])

  const getDisplayName = () => {
    if (!user) return "Guest"

    if (user.is_superuser) {
      return "Super Admin"
    } else {
      const profileToUse = currentProfile?.person || user.person
      if (profileToUse) {
        return profileToUse.full_name || `${profileToUse.first_name} ${profileToUse.last_name}` || user.email || "User"
      } else {
        return user.email || "User"
      }
    }
  }

  const getRoleName = () => {
    if (!user || !user.roles || user.roles.length === 0) return null
    return user.roles[0]?.name
  }

  const handleModalClose = () => {
    setModalDismissed(true)
  }

  const displayName = getDisplayName()
  const roleName = getRoleName()
  const shouldShowModal = profileIncomplete && !modalDismissed && !isOnProfilePage

  return (
    <>
      <Col xl="5" lg="4" md="4" sm="3" className="left-header p-0">
        <div>
          <Link className="toggle-sidebar" to={Href}>
            <i className="iconly-Category"></i>
          </Link>
          <div className="d-flex align-items-center gap-2">
            <H4 className="f-w-600">Welcome {displayName}</H4>
            <Image className="mt-0" src={dynamicImage("hand.gif") || "/placeholder.svg"} alt="hand-gif" />
            {roleName && <span className="badge bg-primary bg-opacity-10 text-primary ms-2 fw-normal">{roleName}</span>}
          </div>
        </div>
        <div className="welcome-content d-xl-block d-none">
          <Col xs="12" className="text-truncate">
            Here's what's happening today.
          </Col>
        </div>
      </Col>

      <ProfileCompletionModal isOpen={shouldShowModal} onClose={handleModalClose} />
    </>
  )
}

export default LeftHeader
